import {
    BUILDING_DESCRIPTION_ADDRESS_AND_TYPE_FIELDS,
    BUILDING_DESCRIPTION_ADDRESS_FIELDS,
    BUILDING_DESCRIPTION_HEATING_HOT_WATER_FIELDS,
    BUILDING_DESCRIPTION_PARKING_SPACE_FIELDS,
    BUILDING_DESCRIPTION_RENOVATION_FIELDS,
    BUILDING_DESCRIPTION_RENOVATION_SECTION_FIELDS,
    BUILDING_DESCRIPTION_ROOF_BASEMENT_WINDOW_FIELDS,
    LOAN_REQUEST_COMMANDS,
} from 'cpool-constants';

import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema.ts';
import { generateDiffRealEstates } from '~/features/leadDetails/utils/compareRealEstates.ts';

type RealEstate = SchemaRealEstatesForm['immobilien'][number];
type RealEstateData = Omit<RealEstate, 'baubeschreibung'>;
type Loan = RealEstate['darlehen'][number];

type CommandObject =
    | {
          commandName: typeof LOAN_REQUEST_COMMANDS.addRealEstate;
          payload: { id: string } & SchemaRealEstatesForm['immobilien'][number];
      }
    | {
          commandName: typeof LOAN_REQUEST_COMMANDS.removeRealEstate;
          payload: {
              id: string;
              gehoertZuAntragsteller: SchemaRealEstatesForm['immobilien'][number]['gehoertZuAntragsteller'];
          };
      }
    | {
          commandName: typeof LOAN_REQUEST_COMMANDS.changeRealEstate;
          payload: { id: string } & Partial<RealEstateData>;
      }
    | {
          commandName: typeof LOAN_REQUEST_COMMANDS.changeRealEstateBuildingDescription;
          payload: Pick<RealEstate, 'baubeschreibung' | 'gehoertZuAntragsteller' | 'id'>;
      };

const REAL_ESTATE_FIELDS = [
    'baufinanzierungsrate',
    'bezeichnung',
    'gehoertZuAntragsteller',
    'immobilienart',
    'mieteinnahmenKaltMonatlich',
    'mieteinnahmenWarmMonatlich',
    'nebenkostenMonatlich',
    'nutzungsart',
    'vermieteteWohnflaeche',
    'wert',
    'wohnflaeche',
] as const satisfies ReadonlyArray<keyof RealEstate>;

const LOAN_FIELDS = ['rateMonatlich', 'restschuld'] as const satisfies ReadonlyArray<keyof Loan>;

function fieldsChanged<T, TField extends keyof T>(previous: T, current: T, fields: readonly TField[]): boolean {
    return fields.some((field) => previous[field] !== current[field]);
}

function listChanged<T>(
    previous: readonly T[],
    current: readonly T[],
    entryChanged: (previousEntry: T, currentEntry: T) => boolean,
): boolean {
    return (
        previous.length !== current.length ||
        previous.some((previousEntry, index) => entryChanged(previousEntry, current[index]))
    );
}

function loanChanged(previous: Loan, current: Loan): boolean {
    return (
        fieldsChanged(previous, current, LOAN_FIELDS) ||
        previous.zinsbindungBis?.getTime() !== current.zinsbindungBis?.getTime()
    );
}

function realEstateDataChanged(previous: RealEstate, current: RealEstate): boolean {
    return (
        fieldsChanged(previous, current, REAL_ESTATE_FIELDS) ||
        listChanged(previous.darlehen, current.darlehen, loanChanged)
    );
}

function buildingDescriptionChanged(
    previous: RealEstate['baubeschreibung'],
    current: RealEstate['baubeschreibung'],
): boolean {
    if (previous === current) return false;
    if (previous == null) return current != null;
    if (current == null) return true;

    const previousAddressAndType = previous.anschriftUndObjektart;
    const currentAddressAndType = current.anschriftUndObjektart;
    const previousSanitary = previous.sanitaerGaragen;
    const currentSanitary = current.sanitaerGaragen;
    const previousRenovations = previous.sanierungen;
    const currentRenovations = current.sanierungen;

    return (
        fieldsChanged(previousAddressAndType, currentAddressAndType, BUILDING_DESCRIPTION_ADDRESS_AND_TYPE_FIELDS) ||
        fieldsChanged(
            previousAddressAndType.anschrift,
            currentAddressAndType.anschrift,
            BUILDING_DESCRIPTION_ADDRESS_FIELDS,
        ) ||
        fieldsChanged(
            previous.dachKellerFenster,
            current.dachKellerFenster,
            BUILDING_DESCRIPTION_ROOF_BASEMENT_WINDOW_FIELDS,
        ) ||
        listChanged(
            previous.dachKellerFenster.fenster,
            current.dachKellerFenster.fenster,
            (previousWindowType, currentWindowType) => previousWindowType !== currentWindowType,
        ) ||
        fieldsChanged(
            previous.heizungWarmwasser,
            current.heizungWarmwasser,
            BUILDING_DESCRIPTION_HEATING_HOT_WATER_FIELDS,
        ) ||
        listChanged(
            previous.heizungWarmwasser.heizungsart,
            current.heizungWarmwasser.heizungsart,
            (previousHeatingType, currentHeatingType) => previousHeatingType !== currentHeatingType,
        ) ||
        listChanged(
            previous.heizungWarmwasser.energietraeger,
            current.heizungWarmwasser.energietraeger,
            (previousEnergySource, currentEnergySource) => previousEnergySource !== currentEnergySource,
        ) ||
        listChanged(
            previousSanitary.sanitaereEinrichtung,
            currentSanitary.sanitaereEinrichtung,
            (previousFeature, currentFeature) => previousFeature !== currentFeature,
        ) ||
        listChanged(
            previousSanitary.garagenStellplaetze,
            currentSanitary.garagenStellplaetze,
            (previousParkingSpace, currentParkingSpace) =>
                fieldsChanged(previousParkingSpace, currentParkingSpace, BUILDING_DESCRIPTION_PARKING_SPACE_FIELDS),
        ) ||
        fieldsChanged(previousRenovations, currentRenovations, BUILDING_DESCRIPTION_RENOVATION_SECTION_FIELDS) ||
        listChanged(
            previousRenovations.sanierungen,
            currentRenovations.sanierungen,
            (previousRenovation, currentRenovation) =>
                fieldsChanged(previousRenovation, currentRenovation, BUILDING_DESCRIPTION_RENOVATION_FIELDS),
        )
    );
}

export function generateRealEstateCommands(
    oldData: SchemaRealEstatesForm,
    newData: SchemaRealEstatesForm,
): CommandObject[] {
    const differences = generateDiffRealEstates({ immobilien: oldData.immobilien }, newData);
    const previousRealEstatesById = new Map(oldData.immobilien.map((realEstate) => [realEstate.id, realEstate]));
    const addCommands = Object.values(differences.added)
        .flat()
        .map<CommandObject>((payload) => ({
            commandName: LOAN_REQUEST_COMMANDS.addRealEstate,
            payload: {
                ...payload,
                baubeschreibung: null,
            },
        }));
    const modifyCommands = Object.values(differences.modified)
        .flat()
        .filter((payload) => {
            const previous = previousRealEstatesById.get(payload.id);
            return previous ? realEstateDataChanged(previous, payload) : false;
        })
        .map<CommandObject>((payload) => {
            const { baubeschreibung: _baubeschreibung, ...realEstateData } = payload;
            return {
                commandName: LOAN_REQUEST_COMMANDS.changeRealEstate,
                payload: realEstateData,
            };
        });
    const buildingDescriptionCommands = newData.immobilien.flatMap<CommandObject>((realEstate) => {
        const previous = previousRealEstatesById.get(realEstate.id);
        const descriptionChanged = previous
            ? buildingDescriptionChanged(previous.baubeschreibung, realEstate.baubeschreibung)
            : realEstate.baubeschreibung != null;
        if (!descriptionChanged) return [];

        return [
            {
                commandName: LOAN_REQUEST_COMMANDS.changeRealEstateBuildingDescription,
                payload: {
                    baubeschreibung: realEstate.baubeschreibung,
                    gehoertZuAntragsteller: realEstate.gehoertZuAntragsteller,
                    id: realEstate.id,
                },
            },
        ];
    });
    const removeCommands = Object.values(differences.removed)
        .flat()
        .map<CommandObject>((payload) => ({
            commandName: LOAN_REQUEST_COMMANDS.removeRealEstate,
            payload: { gehoertZuAntragsteller: payload.gehoertZuAntragsteller, id: payload.id },
        }));
    return [...addCommands, ...modifyCommands, ...buildingDescriptionCommands, ...removeCommands];
}
